import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

const ENTITY_OPTIONS = [
  { key: "all", label: "All" },
  { key: "announcement", label: "Announcements" },
  { key: "report", label: "Reports" },
  { key: "property", label: "Properties" },
  { key: "tenant", label: "Tenants" },
];

export default function AdminArchiveCenter() {
  const [entity, setEntity] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [workingKey, setWorkingKey] = useState("");
  const [toast, setToast] = useState(null);
  const [data, setData] = useState({
    summary: {},
    announcements: [],
    reports: [],
    properties: [],
    tenants: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/archive-center", {
        params: {
          entity: entity === "all" ? undefined : entity,
          q: q || undefined,
          limit: 100,
        },
      });
      setData(res.data || {
        summary: {},
        announcements: [],
        reports: [],
        properties: [],
        tenants: [],
      });
    } catch (error) {
      console.error(error);
      setData({
        summary: {},
        announcements: [],
        reports: [],
        properties: [],
        tenants: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, q]);

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timeout);
  }, [toast]);

  const rows = useMemo(() => {
    const list = [];

    (data.announcements || []).forEach((item) => {
      list.push({
        type: "announcement",
        id: item.id,
        title: item.title,
        subtitle: item.message,
        archived_at: item.archived_at,
      });
    });

    (data.reports || []).forEach((item) => {
      list.push({
        type: "report",
        id: item.id,
        title: item.subject,
        subtitle: item.tenant_name,
        archived_at: item.archived_at,
      });
    });

    (data.properties || []).forEach((item) => {
      list.push({
        type: "property",
        id: item.id,
        title: item.property_name,
        subtitle: item.location,
        archived_at: item.archived_at,
      });
    });

    (data.tenants || []).forEach((item) => {
      list.push({
        type: "tenant",
        id: item.id,
        title: item.name,
        subtitle: item.email,
        archived_at: item.archived_at,
      });
    });

    return list.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at));
  }, [data]);

  const restoreItem = async (item) => {
    const key = `${item.type}:${item.id}`;
    try {
      setWorkingKey(key);
      await api.put("/admin/archive-center/restore", {
        entity_type: item.type,
        entity_id: item.id,
      });
      setToast({ message: `${item.type} restored.`, type: "success" });
      await load();
    } catch (error) {
      console.error(error);
      setToast({ message: `Failed to restore ${item.type}.`, type: "error" });
    } finally {
      setWorkingKey("");
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true" src={bgVideo} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 border-b border-white/10 pb-5">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">Admin Archives</p>
          <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">Archive Center</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
            Restore archived records for announcements, reports, properties, and tenants from one place.
          </p>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-3 text-center lg:grid-cols-4">
          {[
            ["Announcements", data.summary?.announcements || 0, "text-amber-200"],
            ["Reports", data.summary?.reports || 0, "text-red-200"],
            ["Properties", data.summary?.properties || 0, "text-sky-200"],
            ["Tenants", data.summary?.tenants || 0, "text-teal-200"],
          ].map(([label, value, color]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-white/5 px-4 py-3">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
            </div>
          ))}
        </div>

        <div className="mb-5 grid gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center">
          <div className="flex flex-wrap gap-2">
            {ENTITY_OPTIONS.map((option) => (
              <button
                key={option.key}
                onClick={() => setEntity(option.key)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  entity === option.key
                    ? "bg-teal-600 text-white"
                    : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            placeholder="Search archived records"
          />
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading archived records...
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 py-10 text-center text-slate-300">
            No archived records found.
          </div>
        ) : (
          <div className="grid gap-3">
            {rows.map((item) => {
              const key = `${item.type}:${item.id}`;
              return (
                <article key={key} className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{item.type}</p>
                      <h3 className="mt-1 text-lg font-bold text-white">{item.title || "-"}</h3>
                      <p className="mt-1 text-sm text-slate-300">{item.subtitle || "-"}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Archived: {item.archived_at ? new Date(item.archived_at).toLocaleString() : "-"}
                      </p>
                    </div>
                    <button
                      onClick={() => restoreItem(item)}
                      disabled={workingKey === key}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {workingKey === key ? "Restoring..." : "Restore"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <div
          className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
            toast.type === "success" ? "bg-teal-600" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

